import {useEffect, useMemo, useState} from 'react'
import {Heading} from '@/components/tailwind/heading'
import {MemberPhoneListItem} from '@/models/member-phone-list'
import {exportPhoneListExcel, getPhoneList} from '@/services/member-phone-list-services'
import {useAuth} from '@/context/use-auth'
import {useReportsTranslation, useCommonTranslation} from '@/i18n/hooks'
import {Text} from '@/components/tailwind/text'
import {Input, InputGroup} from '@/components/tailwind/input'
import {MagnifyingGlassIcon} from '@heroicons/react/16/solid'
import {MemberPhoneListDataTable} from './member-phone-list-data-table'
import {createPhoneListColumns} from './member-phone-list-columns'
import {Button} from '@/components/tailwind/button'
import {FileSpreadsheet} from 'lucide-react'
import {useToast} from '@/hooks/use-toast'
import log from '@/lib/logger'

function MemberPhoneListPage() {
    const [members, setMembers] = useState<MemberPhoneListItem[]>([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [searchTerm, setSearchTerm] = useState('')
    const {token} = useAuth()
    const {t} = useReportsTranslation()
    const {t: tCommon} = useCommonTranslation()
    const {toast} = useToast()

    useEffect(() => {
        async function fetchData() {
            if (!token) {
                log.error('No token available for fetching phone list')
                setLoading(false)
                return
            }

            try {
                setLoading(true)
                setError(null)
                const data = await getPhoneList(token)
                setMembers(data)
            } catch (err) {
                log.error('Error fetching phone list:', err)
                setError(t('phoneList.error'))
            } finally {
                setLoading(false)
            }
        }

        fetchData()
    }, [token])

    const columns = useMemo(() => createPhoneListColumns(), [])

    async function handleExportExcel() {
        if (!token) return

        try {
            const {blob, filename} = await exportPhoneListExcel(token)
            const url = window.URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.href = url
            a.download = filename
            document.body.appendChild(a)
            a.click()
            document.body.removeChild(a)
            window.URL.revokeObjectURL(url)
        } catch (err) {
            log.error('Error downloading Excel:', err)
            toast({
                title: t('phoneList.export.error'),
                description: t('phoneList.export.errorDescription'),
                variant: 'destructive'
            })
        }
    }

    if (loading) {
        return (
            <div className="container mx-auto py-6">
                <Text>{tCommon('loading')}</Text>
            </div>
        )
    }

    if (error) {
        return (
            <div className="container mx-auto py-6">
                <Text className="text-red-500">{error}</Text>
            </div>
        )
    }

    return (
        <div className="container mx-auto py-6 space-y-6">
            <div className="flex items-start justify-between">
                <div>
                    <Heading>{t('phoneList.title')}</Heading>
                    <Text>{t('phoneList.description')}</Text>
                </div>
                <Button
                    plain
                    onClick={handleExportExcel}
                    className="flex items-center gap-2"
                    title={t('phoneList.export.excel')}
                >
                    <FileSpreadsheet className="h-4 w-4"/>
                    <span className="hidden sm:inline">{t('phoneList.export.excel')}</span>
                </Button>
            </div>

            <div>
                <InputGroup>
                    <MagnifyingGlassIcon/>
                    <Input
                        type="text"
                        placeholder={t('phoneList.search.placeholder')}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        aria-label={t('phoneList.search.ariaLabel')}
                    />
                </InputGroup>
            </div>

            {members.length === 0 ? (
                <Text className="italic text-gray-500">{t('phoneList.noMembers')}</Text>
            ) : (
                <MemberPhoneListDataTable
                    columns={columns}
                    data={members}
                    searchTerm={searchTerm}
                />
            )}
        </div>
    )
}

export default MemberPhoneListPage
